import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { ArtistTierDefinition, ArtistApplication, ProfileResponse } from '../types';
import './BecomeAnArtist.css';

const TIER_ORDER = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Elite'];

const BecomeAnArtist: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const [tiers, setTiers] = useState<ArtistTierDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<ArtistApplication | null>(null);
  const [hasArtistProfile, setHasArtistProfile] = useState(false);

  useEffect(() => {
    const fetchTiers = async (): Promise<void> => {
      try {
        const response = await api.get<ArtistTierDefinition[]>('/artist-tiers');
        const list = Array.isArray(response.data) ? response.data : [];
        setTiers(list.sort((a, b) => TIER_ORDER.indexOf(a.name) - TIER_ORDER.indexOf(b.name)));
      } catch (e) {
        console.error('Failed to load tiers', e);
        setTiers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTiers();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    const fetchProfile = async (): Promise<void> => {
      try {
        const response = await api.get<ProfileResponse>('/profile/me');
        setHasArtistProfile(!!response.data.artistProfile);
      } catch {
        setHasArtistProfile(false);
      }
    };
    const fetchApplication = async (): Promise<void> => {
      try {
        const response = await api.get<ArtistApplication>('/artist-applications/me');
        setApplication(response.data);
      } catch {
        setApplication(null);
      }
    };
    fetchProfile();
    fetchApplication();
  }, [isAuthenticated, user]);

  if (loading) {
    return (
      <div className="become-artist-page">
        <div className="container">
          <p className="become-artist-loading">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="become-artist-page">
      <div className="container">
        <header className="become-artist-header">
          <h1>Become a Phi Artist</h1>
          <p className="become-artist-lead">
            Sell your art as prints on Phi. Your tier is based on <strong>lifetime earnings</strong> from orders.
            Higher tiers unlock a bigger discount on your own orders and more artwork slots.
          </p>
        </header>

        <section className="become-artist-tiers">
          <h2>Tier benefits</h2>
          <div className="tier-table-wrapper">
            <table className="tier-table">
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Lifetime earnings</th>
                  <th>Your discount</th>
                  <th>Artwork slots</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {tiers.map((tier) => (
                  <tr key={tier.name} className={`tier-row tier-${tier.name.toLowerCase()}`}>
                    <td><span className="tier-badge">{tier.name}</span></td>
                    <td>${tier.minLifetimeEarnings.toLocaleString()}+</td>
                    <td>{tier.discountPercent}%</td>
                    <td>{tier.artworkUploadLimit}</td>
                    <td className="tier-desc">{tier.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="become-artist-apply">
          <h2>Apply to the program</h2>
          {hasArtistProfile ? (
            <div className="become-artist-status-card status-approved">
              <p>You&apos;re already a Phi Artist.</p>
              <Link to="/profile" className="btn btn-primary">Go to Profile</Link>
              <Link to="/artist/metrics" className="btn btn-secondary">View my tier &amp; metrics</Link>
            </div>
          ) : application ? (
            <div className={`become-artist-status-card status-${application.status.toLowerCase()}`}>
              <p><strong>Application status:</strong> {application.status}</p>
              {application.adminNotes && <p><strong>Notes:</strong> {application.adminNotes}</p>}
              <p>Submitted: {new Date(application.createdAt).toLocaleDateString()}</p>
              {(application.status === 'Pending' || application.status === 'Rejected') && (
                <Link to="/profile" className="btn btn-primary">Update application or view details</Link>
              )}
            </div>
          ) : isAuthenticated ? (
            <div className="become-artist-cta">
              <p>Submit an application from your profile. We&apos;ll review it and approve you to start at <strong>Bronze</strong>.</p>
              <Link to="/profile" className="btn btn-primary">Apply from Profile</Link>
            </div>
          ) : (
            <div className="become-artist-cta">
              <p>Create an account, then apply from your profile to join as a Phi Artist (Bronze tier).</p>
              <Link to="/register" className="btn btn-primary">Create account</Link>
              <Link to="/login" className="btn btn-secondary">Log in</Link>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default BecomeAnArtist;
